import { useState, useEffect, useRef } from "react";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg } from "@/lib/api";
import { PageHeader } from "@/components/common";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Switch } from "@/components/ui/switch";
import { LogOut, User, ShieldCheck, AtSign, Hash, ImagePlus, Clock, TrendingUp } from "lucide-react";
import { toast } from "sonner";
import { AssistanceChart } from "@/components/AssistanceChart";

const roleLabel = { paziente: "Paziente", fisioterapista: "Fisioterapista", admin: "Amministratore" };
const DAYS = [
  { key: "0", label: "Lunedì" },
  { key: "1", label: "Martedì" },
  { key: "2", label: "Mercoledì" },
  { key: "3", label: "Giovedì" },
  { key: "4", label: "Venerdì" },
  { key: "5", label: "Sabato" },
  { key: "6", label: "Domenica" },
];

export default function Profile() {
  const { user, logout, refresh } = useAuth();
  const isPhysio = user?.role === "fisioterapista";
  const isAdmin = user?.role === "admin";
  const isPatient = user?.role === "paziente";
  const fileRef = useRef(null);
  const [logo, setLogo] = useState(user?.logo_base64 || "");
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [avail, setAvail] = useState({});
  const [savingAvail, setSavingAvail] = useState(false);
  const [assist, setAssist] = useState(null);
  const [trend, setTrend] = useState(null);
  const [physioBreak, setPhysioBreak] = useState(null);

  useEffect(() => {
    if (!isPhysio) return;
    api.getAvailability().then((d) => setAvail(d?.availability || {})).catch(() => {});
  }, [isPhysio]);

  useEffect(() => {
    if (isPatient) { api.patientAssistance(user.id).then(setAssist).catch(() => {}); return; }
    if (!isPhysio && !isAdmin) return;
    const fetcher = isAdmin ? api.adminAssistance : api.physioAssistance;
    const val = (d) => (isAdmin ? d?.totals : d) || { prescribed_minutes: 0, executed_minutes: 0 };
    Promise.all([fetcher("week"), fetcher("month"), fetcher("all")]).then(([w, m, a]) => {
      setAssist(val(m));
      setTrend([
        { label: "Settimana", prescribed: val(w).prescribed_minutes, executed: val(w).executed_minutes },
        { label: "Mese", prescribed: val(m).prescribed_minutes, executed: val(m).executed_minutes },
        { label: "Totale", prescribed: val(a).prescribed_minutes, executed: val(a).executed_minutes },
      ]);
      if (isAdmin) setPhysioBreak((m?.physios || []).map((p) => ({ label: p.physio_name, prescribed: p.prescribed_minutes, executed: p.executed_minutes })));
    }).catch(() => {});
  }, [isPhysio, isAdmin, isPatient, user?.id]);

  const initials = (user?.full_name || user?.username || "?").split(" ").map((s) => s[0]).slice(0, 2).join("").toUpperCase();

  const onLogoFile = (file) => {
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) { toast.error("Immagine troppo grande (max 2MB)"); return; }
    const reader = new FileReader();
    reader.onload = async () => {
      const dataUrl = reader.result;
      setUploadingLogo(true);
      try {
        await api.updateLogo(dataUrl);
        setLogo(dataUrl);
        toast.success("Immagine aggiornata — visibile ai tuoi pazienti");
        refresh?.();
      } catch (e) { toast.error(errMsg(e)); }
      finally { setUploadingLogo(false); }
    };
    reader.readAsDataURL(file);
  };

  const saveAvail = async () => {
    setSavingAvail(true);
    try {
      await api.updateAvailability(avail);
      toast.success("Orari di ricevimento salvati");
    } catch (e) { toast.error(errMsg(e)); }
    finally { setSavingAvail(false); }
  };

  const rows = [
    { icon: User, label: "Nome completo", value: user?.full_name || "—" },
    { icon: AtSign, label: "Username", value: user?.username },
    { icon: ShieldCheck, label: "Ruolo", value: roleLabel[user?.role] || user?.role },
    { icon: Hash, label: "ID utente", value: user?.id },
  ];

  return (
    <div className="mx-auto max-w-2xl">
      <PageHeader title="Profilo" subtitle="I tuoi dati account." />

      <Card className="s2m-hero-gradient mb-6 border-none text-white">
        <CardContent className="flex items-center gap-4 p-6">
          <Avatar className="h-16 w-16 bg-white/20">
            {logo ? <AvatarImage src={logo} alt="Immagine profilo" /> : null}
            <AvatarFallback className="bg-transparent text-lg font-semibold text-white">{initials}</AvatarFallback>
          </Avatar>
          <div>
            <p className="text-xl font-bold">{user?.full_name || user?.username}</p>
            <p className="text-sm text-white/85">{roleLabel[user?.role] || user?.role}</p>
          </div>
        </CardContent>
      </Card>

      {(isPhysio || isAdmin || isPatient) && (
        <Card className="mb-6 border-border/70" data-testid="assistance-stat-card">
          <CardHeader><CardTitle className="flex items-center gap-2 text-lg"><Clock className="h-5 w-5 text-primary" /> Minuti assistenziali</CardTitle></CardHeader>
          <CardContent className="grid grid-cols-2 gap-3">
            <div className="rounded-2xl bg-[#eef4fb] p-4">
              <p className="text-3xl font-bold text-[#1f3a5f]" data-testid="assist-prescribed">{assist?.prescribed_minutes ?? 0}</p>
              <p className="text-sm text-muted-foreground">{isPatient ? "Da svolgere (min) · questo mese" : `Prescritti (min)${isAdmin ? " · tutti i pazienti" : " · questo mese"}`}</p>
            </div>
            <div className="rounded-2xl bg-[#e6f0e9] p-4">
              <p className="text-3xl font-bold text-[#1f3a5f]" data-testid="assist-executed">{assist?.executed_minutes ?? 0}</p>
              <p className="text-sm text-muted-foreground">Eseguiti (min)</p>
            </div>
          </CardContent>
        </Card>
      )}

      {(isPhysio || isAdmin) && trend && (
        <Card className="mb-6 border-border/70" data-testid="assistance-trend-card">
          <CardHeader><CardTitle className="flex items-center gap-2 text-lg"><TrendingUp className="h-5 w-5 text-primary" /> Andamento minuti assistenziali</CardTitle></CardHeader>
          <CardContent>
            <p className="mb-3 text-sm text-muted-foreground">Confronto minuti prescritti vs eseguiti per periodo.</p>
            <AssistanceChart data={trend} testId="assistance-trend-chart" />
          </CardContent>
        </Card>
      )}

      {isAdmin && physioBreak && physioBreak.length > 0 && (
        <Card className="mb-6 border-border/70" data-testid="physio-breakdown-card">
          <CardHeader><CardTitle className="flex items-center gap-2 text-lg"><TrendingUp className="h-5 w-5 text-primary" /> Minuti per fisioterapista · questo mese</CardTitle></CardHeader>
          <CardContent>
            <AssistanceChart data={physioBreak} testId="physio-breakdown-chart" />
          </CardContent>
        </Card>
      )}

      {isPhysio && (
        <Card className="mb-6 border-border/70" data-testid="physio-logo-card">
          <CardHeader><CardTitle className="text-lg">Immagine profilo</CardTitle></CardHeader>
          <CardContent className="flex flex-wrap items-center gap-4">
            <Avatar className="h-20 w-20 bg-primary/15">
              {logo ? <AvatarImage src={logo} alt="Immagine profilo" /> : null}
              <AvatarFallback className="bg-transparent text-xl font-semibold text-primary">{initials}</AvatarFallback>
            </Avatar>
            <div className="flex-1">
              <p className="text-sm text-muted-foreground">Questa immagine sarà visibile ai tuoi pazienti nella chat.</p>
              <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={(e) => onLogoFile(e.target.files?.[0])} data-testid="physio-logo-input" />
              <Button variant="outline" className="mt-3" disabled={uploadingLogo} onClick={() => fileRef.current?.click()} data-testid="physio-logo-upload-btn">
                <ImagePlus className="mr-2 h-4 w-4" /> {uploadingLogo ? "Caricamento..." : "Carica immagine"}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {isPhysio && (
        <Card className="mb-6 border-border/70" data-testid="physio-availability-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg"><Clock className="h-5 w-5 text-primary" /> Orari di ricevimento in chat</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <p className="text-sm text-muted-foreground">Attiva i giorni e indica la fascia oraria in cui rispondi in chat in diretta. Sarà mostrata ai tuoi pazienti.</p>
            <div className="space-y-2">
              {DAYS.map(({ key, label }) => {
                const day = avail[key] || { enabled: false, start: "09:00", end: "18:00" };
                const set = (patch) => setAvail({ ...avail, [key]: { ...day, ...patch } });
                return (
                  <div key={key} className="flex flex-wrap items-center gap-3 rounded-lg border border-border/60 p-2.5" data-testid={`availability-${key}`}>
                    <div className="flex w-28 items-center gap-2">
                      <Switch checked={!!day.enabled} onCheckedChange={(v) => set({ enabled: v })} data-testid={`availability-toggle-${key}`} />
                      <Label className="text-sm">{label}</Label>
                    </div>
                    {day.enabled && (
                      <div className="flex items-center gap-2">
                        <Input type="time" value={day.start || "09:00"} onChange={(e) => set({ start: e.target.value })} className="w-32" data-testid={`availability-start-${key}`} />
                        <span className="text-muted-foreground">–</span>
                        <Input type="time" value={day.end || "18:00"} onChange={(e) => set({ end: e.target.value })} className="w-32" data-testid={`availability-end-${key}`} />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
            <Button onClick={saveAvail} disabled={savingAvail} data-testid="availability-save-btn">{savingAvail ? "Salvataggio..." : "Salva orari"}</Button>
          </CardContent>
        </Card>
      )}

      <Card className="border-border/70">
        <CardContent className="divide-y p-0" data-testid="profile-info">
          {rows.map((r, i) => (
            <div key={i} className="flex items-center gap-3 p-4">
              <div className="grid h-9 w-9 place-items-center rounded-xl bg-primary/10 text-primary"><r.icon className="h-4 w-4" /></div>
              <div className="min-w-0">
                <p className="text-xs uppercase tracking-wide text-muted-foreground">{r.label}</p>
                <p className="truncate text-sm font-medium text-[#1f3a5f]">{r.value}</p>
              </div>
            </div>
          ))}
        </CardContent>
      </Card>

      <Button variant="outline" className="mt-6 w-full text-destructive" onClick={logout} data-testid="profile-logout-button">
        <LogOut className="mr-2 h-4 w-4" /> Esci
      </Button>

      <p className="mt-6 text-center text-xs text-muted-foreground">
        Per supporto tecnico scrivere a{" "}
        <a href="mailto:science2movement@gmail.com" className="font-medium text-primary">science2movement@gmail.com</a>
      </p>
    </div>
  );
}
